
import React from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Edit, Trash2, Clock, Package, Hash, Palette, FileText, Download } from "lucide-react";
import { MessageProps } from "./types";

const ProductCard: React.FC<{ product: NonNullable<MessageProps["productInfo"]>; messageText: string; isCurrentUser: boolean }> = ({ product, messageText, isCurrentUser }) => {
  // Extract the message part after "SKU: "
  const colonIndex = messageText.indexOf(":");
  const userMessage = colonIndex > -1 ? messageText.slice(colonIndex + 1).trim() : messageText;

  return (
    <div className={`rounded-lg overflow-hidden border max-w-[320px] ${isCurrentUser ? "bg-green-100 dark:bg-green-900/30" : "bg-blue-100 dark:bg-blue-900/30"}`}>
      {/* Product image */}
      {product.image_url ? (
        <div className="w-full h-40 bg-muted">
          <img
            src={product.image_url}
            alt={product.product_name}
            className="w-full h-full object-contain"
          />
        </div>
      ) : (
        <div className="w-full h-28 bg-muted flex items-center justify-center">
          <Package className="h-10 w-10 text-muted-foreground" />
        </div>
      )}
      
      {/* Product details */}
      <div className="p-3 space-y-1.5">
        <p className="font-medium text-sm leading-tight">{product.product_name}</p>
        
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Hash className="h-3 w-3" />
            {product.product_sku}
          </span>
          {product.product_quantity && (
            <span>Aantal: {product.product_quantity}</span>
          )}
          {product.product_color && (
            <span className="flex items-center gap-1">
              <Palette className="h-3 w-3" />
              {product.product_color}
            </span>
          )}
        </div>

        {userMessage && (
          <p className="text-sm mt-1.5 pt-1.5 border-t border-border">
            {userMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export const ChatMessage: React.FC<MessageProps> = ({
  message,
  formatTimestamp,
  getInitials,
  getMessageClasses,
  onEdit,
  onDelete,
  isCurrentUser,
  editingMessage,
  editText,
  setEditText,
  setEditingMessage,
  productInfo
}) => {
  return (
    <div key={message.id} className="flex flex-col gap-1">
      <div className="flex items-start gap-3">
        <Avatar className="h-8 w-8">
          <AvatarFallback>{getInitials(message.user_name)}</AvatarFallback>
        </Avatar>
        
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">
              {isCurrentUser ? `${message.user_name} (jij)` : message.user_name}
            </span>
            <span className="text-xs text-muted-foreground flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {formatTimestamp(message.created_at)}
            </span>
          </div>
          
          {editingMessage === message.id ? (
            <div className="flex flex-col gap-2">
              <Textarea 
                value={editText} 
                onChange={(e) => setEditText(e.target.value)}
                className="min-h-[60px]"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => onEdit(message.id, editText)}>
                  Opslaan
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => {
                    setEditingMessage(null);
                    setEditText("");
                  }}
                >
                  Annuleren
                </Button>
              </div>
            </div>
          ) : productInfo && !message.is_deleted ? (
            <ProductCard product={productInfo} messageText={message.message} isCurrentUser={isCurrentUser} />
          ) : message.attachment_url && !message.is_deleted ? (
            <div>
              <div className={`rounded-lg overflow-hidden border max-w-[320px] ${isCurrentUser ? "bg-green-100 dark:bg-green-900/30" : "bg-blue-100 dark:bg-blue-900/30"}`}>
                {message.attachment_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  <a href={message.attachment_url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={message.attachment_url}
                      alt="Bijlage"
                      className="w-full object-contain max-h-72"
                    />
                  </a>
                ) : (
                  <a
                    href={message.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 text-sm text-primary hover:underline"
                  >
                    <FileText className="h-5 w-5" />
                    <span>Bestand bekijken</span>
                    <Download className="h-4 w-4" />
                  </a>
                )}
                {message.message && (
                  <p className="text-sm p-3 border-t border-border">{message.message}</p>
                )}
              </div>
              {message.is_edited && (
                <span className="text-xs text-muted-foreground block mt-1">
                  (bewerkt op {formatTimestamp(message.updated_at)})
                </span>
              )}
            </div>
          ) : (
            <div className={getMessageClasses(message)}>
              {message.message && <p>{message.message}</p>}
              {message.is_edited && !message.is_deleted && (
                <span className="text-xs text-muted-foreground block mt-1">
                  (bewerkt op {formatTimestamp(message.updated_at)})
                </span>
              )}
            </div>
          )}
        </div>
        
        {isCurrentUser && 
         !message.is_deleted && 
         editingMessage !== message.id && (
          <div className="flex gap-1 ml-2">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7" 
              onClick={() => {
                setEditingMessage(message.id);
                setEditText(message.message);
              }}
            >
              <Edit className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7" 
              onClick={() => onDelete(message.id)}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
